import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Check, Copy, Loader2, Mic, RotateCcw, SendHorizontal, Trash2 } from "lucide-react";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { PlatformHeader } from "@/components/abigail/PlatformHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatarDuracao, suportaGravacao, useGravacao } from "@/lib/gravacao";
import mark from "@/assets/abigail-mark.png";

export const Route = createFileRoute("/_authenticated/chat")({
  component: ChatPage,
});

type Conversation = { id: string; title: string; updated_at: string };
type Message = { id: string; role: "user" | "assistant"; content: string };
type EstadoAvaliacoes = {
  concluida: boolean;
  podeIniciarNova: boolean;
  somenteLeitura: boolean;
  restantes: number;
  limite: number;
  usadas: number;
  vozHabilitada: boolean;
  /** null for admins — the 6-month access window doesn't apply to them. */
  diasRestantesAcesso: number | null;
};

/** Months while there's still time to spare; the exact day count once it's close. */
function formatarPrazoAcesso(dias: number): string {
  if (dias <= 30) {
    return `Restam ${dias} dia${dias === 1 ? "" : "s"} de acesso à plataforma.`;
  }
  const meses = Math.round(dias / 30);
  return `Restam ${meses} ${meses === 1 ? "mês" : "meses"} de acesso à plataforma.`;
}

const ESTADO_KEY = ["conversations", "estado"] as const;

let tempIdCounter = 0;

function ChatPage() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [confirmandoNova, setConfirmandoNova] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const userScrolledUp = useRef(false);

  // Ensure conversation exists, then load its messages — runs once
  const ensureConversation = useMutation({
    mutationFn: () => api.post<Conversation>("/api/conversations"),
    onSuccess: async (conversation) => {
      setActiveId(conversation.id);
      try {
        const msgs = await api.get<Message[]>(
          `/api/conversations/${conversation.id}/messages`,
        );
        setMessages(msgs);
      } catch {
        // silent — empty chat is fine
      }
      setLoaded(true);
      composerRef.current?.focus();
    },
  });

  useEffect(() => {
    if (activeId || ensureConversation.isPending || ensureConversation.isSuccess) return;
    ensureConversation.mutate();
  }, [activeId, ensureConversation]);

  // Whether this journey is finished, and whether another one is still available.
  const { data: estado, refetch: recarregarEstado } = useQuery({
    queryKey: ESTADO_KEY,
    queryFn: () => api.get<EstadoAvaliacoes>("/api/conversations/estado"),
    enabled: Boolean(activeId),
  });

  const somenteLeitura = estado?.somenteLeitura ?? false;
  // What the person is left with once they spend the current evaluation.
  const restantesDepois = Math.max(0, (estado?.restantes ?? 0) - 1);

  const gravacao = useGravacao({
    aoTranscrever: async (audio) => {
      const form = new FormData();
      form.append("audio", audio);
      const { texto } = await api.postForm<{ texto: string }>("/api/chat/transcrever", form);
      return texto;
    },
    // The transcript lands in the composer instead of being sent, so the person can
    // fix it first. Appends, so dictating in pieces works.
    aoConcluir: (texto) => {
      setInput((atual) => (atual.trim() ? `${atual.trim()} ${texto}` : texto));
      composerRef.current?.focus();
    },
    aoFalhar: (mensagem) => setError(mensagem),
  });

  const mostrarMicrofone =
    (estado?.vozHabilitada ?? false) && !somenteLeitura && suportaGravacao();
  const gravando = gravacao.estado === "gravando";
  const transcrevendo = gravacao.estado === "transcrevendo";

  const novaAvaliacao = useMutation({
    mutationFn: () => api.post<Conversation>("/api/conversations/nova"),
    onSuccess: async (conversation) => {
      setConfirmandoNova(false);
      setActiveId(conversation.id);
      setError(null);
      setStreaming("");
      try {
        const msgs = await api.get<Message[]>(
          `/api/conversations/${conversation.id}/messages`,
        );
        setMessages(msgs);
      } catch {
        setMessages([]);
      }
      void recarregarEstado();
      composerRef.current?.focus();
    },
    onError: (err) => {
      setConfirmandoNova(false);
      setError(
        err instanceof Error ? err.message : "Não foi possível iniciar uma nova avaliação.",
      );
    },
  });

  // Auto-scroll when near bottom
  useEffect(() => {
    if (!userScrolledUp.current) {
      // Smooth animations queue up and compound while streaming, so only the
      // settled-message case animates.
      bottomRef.current?.scrollIntoView({
        behavior: streaming ? "auto" : "smooth",
      });
    }
  }, [messages, streaming]);

  // Detect if user scrolled up
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledUp.current = distanceFromBottom > 150;
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || !activeId || isSending || somenteLeitura) return;

    setInput("");
    setError(null);
    setIsSending(true);
    setStreaming("");
    userScrolledUp.current = false;

    // Add user message to local state immediately
    const userMsg: Message = {
      id: `temp-user-${++tempIdCounter}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: activeId, message: text }),
      });

      if (!response.ok || !response.body) {
        const payload = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(
          payload?.error ?? "A Abigail não conseguiu responder agora.",
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // Repaint at most ~12x/s. One setState per delta (hundreds per reply) starved
      // the main thread, which stopped draining this stream and stalled the reply.
      let lastPaint = 0;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          acc += decoder.decode(value, { stream: true });
          const now = performance.now();
          if (now - lastPaint > 80) {
            lastPaint = now;
            setStreaming(acc);
          }
        }
      } catch {
        if (!acc) {
          setError("Conexão interrompida. Tente enviar novamente.");
        }
      }

      // Add completed assistant message to local state
      if (acc.trim()) {
        const assistantMsg: Message = {
          id: `temp-assistant-${++tempIdCounter}`,
          role: "assistant",
          content: acc,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      }

      setStreaming("");
      setIsSending(false);
      // Abigail may have just delivered the diagnostic, which closes this journey.
      void recarregarEstado();
      composerRef.current?.focus();
    } catch (sendError) {
      setError(
        sendError instanceof Error
          ? sendError.message
          : "A Abigail não conseguiu responder agora. Tente novamente.",
      );
      setStreaming("");
      setIsSending(false);
      composerRef.current?.focus();
    }
  }, [input, activeId, isSending, somenteLeitura, recarregarEstado]);

  const isEmpty = loaded && messages.length === 0 && !isSending;

  return (
    <div className="relative flex h-dvh flex-col overflow-hidden">
      <AbigailBackdrop dim />
      <PlatformHeader />

      <main className="flex min-h-0 flex-1 flex-col">
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-4xl px-4 py-4 sm:px-6">
            {isEmpty ? (
              <div className="mt-10 text-center">
                <img
                  src={mark}
                  alt=""
                  width={754}
                  height={284}
                  loading="lazy"
                  className="mx-auto h-24 w-auto object-contain mix-blend-screen"
                />
                <h1 className="font-serif text-ember glow-text mt-4 text-4xl">
                  Inteligência Estratégica de Carreira
                </h1>
                <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">
                  Diagnóstico profundo de carreira no audiovisual, na música e
                  na economia criativa na era da IA.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setInput(
                      "Quero me entender na Economia Criativa na Era da IA",
                    );
                    composerRef.current?.focus();
                  }}
                  className="mt-5 inline-block rounded-full border border-border bg-surface/60 px-5 py-2.5 text-sm text-muted-foreground transition-colors hover:border-accent hover:text-foreground"
                >
                  Quero me entender na Economia Criativa na Era da IA
                </button>
              </div>
            ) : null}

            <div className="space-y-5">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  role={message.role}
                  content={message.content}
                />
              ))}
              {isSending ? (
                streaming ? (
                  <MessageBubble role="assistant" content={streaming} />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Abigail está pensando…
                  </div>
                )
              ) : null}
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
            </div>
            <div ref={bottomRef} className="h-4" />
          </div>
        </div>

        <div className="border-t border-border bg-background/70 backdrop-blur-md">
          {estado?.diasRestantesAcesso != null ? (
            <p className="mx-auto w-full max-w-4xl px-4 pt-3 text-center text-xs text-muted-foreground sm:px-6">
              {formatarPrazoAcesso(estado.diasRestantesAcesso)}
            </p>
          ) : null}

          {estado?.podeIniciarNova ? (
            <div className="mx-auto flex w-full max-w-4xl flex-wrap items-center justify-between gap-3 px-4 pt-3 sm:px-6">
              <p className="text-xs text-muted-foreground">
                Sua avaliação está concluída. Você ainda pode realizar{" "}
                {estado.restantes === 1
                  ? "mais uma avaliação"
                  : `mais ${estado.restantes} avaliações`}
                .
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setConfirmandoNova(true)}
              >
                <RotateCcw className="h-4 w-4" />
                Iniciar nova avaliação
              </Button>
            </div>
          ) : null}

          {somenteLeitura ? (
            <div className="mx-auto w-full max-w-4xl px-4 py-4 sm:px-6">
              <p className="text-sm text-muted-foreground">
                Você concluiu todas as suas avaliações com a Abigail. Esta conversa
                continua aqui para você reler quando quiser.
              </p>
            </div>
          ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
            className="mx-auto flex w-full max-w-4xl items-end gap-2 px-4 py-3 sm:px-6"
          >
            {gravando ? (
              <div className="flex h-11 flex-1 items-center gap-3 rounded-md border border-primary/40 bg-surface/70 px-4">
                <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-primary" />
                <span className="text-sm tabular-nums text-foreground">
                  {formatarDuracao(gravacao.segundos)}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  Gravando… toque no visto para transcrever.
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={gravacao.cancelar}
                  aria-label="Descartar gravação"
                  className="ml-auto h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Textarea
                ref={composerRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void send();
                  }
                }}
                rows={1}
                maxLength={8000}
                disabled={transcrevendo}
                placeholder={
                  transcrevendo ? "Transcrevendo seu áudio…" : "Fale com a Abigail…"
                }
                className="max-h-40 min-h-11 resize-none border-border bg-surface/70 text-sm text-foreground placeholder:text-muted-foreground"
              />
            )}

            {mostrarMicrofone && !input.trim() && !isSending ? (
              <Button
                type="button"
                onClick={() => (gravando ? gravacao.parar() : void gravacao.iniciar())}
                disabled={transcrevendo}
                aria-label={gravando ? "Concluir gravação" : "Gravar mensagem de voz"}
                className={
                  gravando
                    ? "bg-ember shadow-ember h-11 w-11 shrink-0 text-primary-foreground hover:opacity-90"
                    : "h-11 w-11 shrink-0 bg-surface/70 text-muted-foreground hover:text-foreground"
                }
              >
                {transcrevendo ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : gravando ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            ) : null}

            <Button
              type="submit"
              disabled={isSending || !input.trim() || !activeId || gravando}
              aria-label="Enviar mensagem"
              className={`bg-ember shadow-ember h-11 w-11 shrink-0 text-primary-foreground hover:opacity-90 ${
                mostrarMicrofone && !input.trim() && !isSending ? "hidden" : ""
              }`}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SendHorizontal className="h-4 w-4" />
              )}
            </Button>
          </form>
          )}
          <p className="pb-2 text-center text-[0.7rem] text-muted-foreground">
            A Abigail pode errar. Confira informações importantes.
          </p>
        </div>
      </main>

      <Dialog open={confirmandoNova} onOpenChange={setConfirmandoNova}>
        <DialogContent className="border-border bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-ember text-xl">
              Iniciar uma nova avaliação?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              Apenas a avaliação mais recente fica disponível para você. Ao começar uma
              nova, <strong className="text-foreground">a avaliação atual será apagada
              e não poderá ser recuperada</strong>.
            </p>
            <p>
              Se quiser guardar o diagnóstico que a Abigail te entregou, copie o texto
              antes de continuar.
            </p>
            {!estado ? null : restantesDepois > 0 ? (
              <p>
                Depois desta, você ainda poderá realizar{" "}
                <strong className="text-foreground">
                  mais {restantesDepois}{" "}
                  {restantesDepois === 1 ? "avaliação" : "avaliações"}
                </strong>
                .
              </p>
            ) : (
              <p>
                Depois desta,{" "}
                <strong className="text-foreground">
                  você não poderá mais realizar avaliações
                </strong>
                .
              </p>
            )}
          </div>

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmandoNova(false)}
              disabled={novaAvaliacao.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-ember shadow-ember gap-2 text-primary-foreground hover:opacity-90"
              onClick={() => novaAvaliacao.mutate()}
              disabled={novaAvaliacao.isPending}
            >
              {novaAvaliacao.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              Apagar e começar de novo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Copy-to-clipboard button shown under a message, with a brief "copied" checkmark. */
function CopiarMensagem({ content, className = "" }: { content: string; className?: string }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(content);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      // Sem acesso à área de transferência — sem feedback, mas nada quebra.
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copiar()}
      aria-label="Copiar mensagem"
      className={`flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground ${className}`}
    >
      {copiado ? (
        <>
          <Check className="h-3.5 w-3.5" /> Copiado
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" /> Copiar
        </>
      )}
    </button>
  );
}

// Memoized: streaming re-renders the page on every repaint, and re-parsing the
// markdown of every settled message each time is what used to freeze long chats.
const MessageBubble = memo(function MessageBubble({
  role,
  content,
}: {
  role: "user" | "assistant";
  content: string;
}) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col items-end gap-1">
          <div className="rounded-2xl border border-border bg-surface/80 px-4 py-3 text-sm leading-relaxed text-foreground">
            {content}
          </div>
          <CopiarMensagem content={content} className="pr-1" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <span className="bg-ember mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-display text-xs text-primary-foreground">
        A
      </span>
      <div className="min-w-0 flex-1">
        <div className="prose-abigail max-w-none text-sm leading-relaxed text-foreground [&_a]:text-accent [&_code]:text-accent [&_h1]:font-serif [&_h2]:font-serif [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-3 [&_strong]:text-accent [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
        <CopiarMensagem content={content} className="mt-1" />
      </div>
    </div>
  );
});
